import { PlusOutlined } from "@ant-design/icons";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { $generateHtmlFromNodes } from "@lexical/html";
import { Select, Tag } from "antd";
import Button from "@ui/form-controls/button";
import {
  $createParagraphNode,
  $createTextNode,
  $createLineBreakNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  createCommand,
  DecoratorNode,
  FORMAT_TEXT_COMMAND,
  KEY_DOWN_COMMAND,
  INSERT_LINE_BREAK_COMMAND,
  LineBreakNode
} from "lexical";
import React, { useEffect, useMemo, useRef } from "react";

/* =======================
 * TagNode (DecoratorNode)
 * ======================= */
class TagNode extends DecoratorNode {
  constructor(label, key) {
    super(key);
    this.__label = label;
  }

  static getType() {
    return "tag";
  }

  static clone(node) {
    return new TagNode(node.__label, node.__key);
  }

  createDOM() {
    const span = document.createElement("span");
    return span;
  }

  updateDOM() {
    return false;
  }

  // Ensure TagNode exports meaningfully to HTML
  exportDOM() {
    const span = document.createElement("span");
    span.setAttribute("data-type", "tag");
    span.setAttribute("data-key", String(this.__label));
    span.className = "email-template-tag";
    // Keep token text so backend parsing remains compatible
    span.textContent = `[#${this.__label}]`;
    return { element: span };
  }

  exportJSON() {
    return {
      type: "tag",
      version: 1,
      label: this.__label
    };
  }

  static importJSON(payload) {
    return new TagNode(payload.label);
  }

  isInline() {
    return true;
  }

  decorate() {
    return <InlineTag label={this.__label} nodeKey={this.getKey()} />;
  }
}

function $createTagNode(label) {
  return new TagNode(label);
}

function $isTagNode(node) {
  return node instanceof TagNode;
}

/* =======================
 * Inline React component
 * ======================= */
function InlineTag(props) {
  const { label, nodeKey } = props;
  const [editor] = useLexicalComposerContext();
  return (
    <Tag
      style={{ marginRight: 4 }}
      data-key={label}
      id="email-template-tag"
      closable
      onClose={e => {
        e.preventDefault();
        editor.update(() => {
          removeTagNodeByKey(nodeKey);
        });
      }}
    >
      {label}
    </Tag>
  );
}

function removeTagNodeByKey(key) {
  const root = $getRoot();
  const stack = [root];
  while (stack.length) {
    const n = stack.pop();
    if (!n) continue;
    if (n.getKey && n.getKey() === key && $isTagNode(n)) {
      n.remove();
      return true;
    }
    if (n.getChildren) stack.push(...n.getChildren());
  }
  return false;
}

/* =======================
 * Commands
 * ======================= */
export const INSERT_TAGS_COMMAND = createCommand();

/* =======================
 * Plugins
 * ======================= */
function TagInsertPlugin({ options, missingFields = [] }) {
  const [editor] = useLexicalComposerContext();

  React.useEffect(() => {
    return editor.registerCommand(
      INSERT_TAGS_COMMAND,
      labels => {
        editor.update(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return;
          (labels || []).forEach(() => {
            // labels can be a string or array; normalize below
          });
          const list = Array.isArray(labels) ? labels : [labels];
          list.forEach(label => {
            const node = $createTagNode(String(label));
            selection.insertNodes([node]);
            selection.insertNodes([$createTextNode(" ")]);
          });
        });
        return true;
      },
      COMMAND_PRIORITY_EDITOR
    );
  }, [editor]);

  return (
    <>
      <div className="email-template-controller-container">
        <span className="email-template-controller-title">Insert Field</span>
        <div className="email-template-controller">
          <Select
            style={{ width: "100%", marginBottom: 8 }}
            mode="multiple"
            placeholder="Insert field"
            options={(options || []).map(o => ({ label: o, value: o }))}
            value={[]}
            onChange={vals => {
              if (typeof editor.focus === "function") {
                editor.focus();
              }
              setTimeout(() => {
                editor.dispatchCommand(INSERT_TAGS_COMMAND, vals);
              }, 0);
            }}
            suffixIcon={<PlusOutlined />}
            showSearch
          />
        </div>

        {missingFields.length > 0 && (
          <div className="fields-error">
            <span className="missing-fields-warning">
              The inserted field must match the selected recipient type{" "}
            </span>
          </div>
        )}
      </div>
    </>
  );
}

/* =======================
 * Text Format Shortcuts
 * ======================= */
function TextFormatShortcutsPlugin({ enableBold = true, enableItalic = true }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      event => {
        const e = event;
        const isAccel = e.ctrlKey || e.metaKey;
        if (!isAccel) return false;
        const key = String(e.key || "").toLowerCase();
        if (enableBold && key === "b") {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
          e.preventDefault();
          return true;
        }
        if (enableItalic && key === "i") {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
          e.preventDefault();
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_EDITOR
    );
  }, [editor, enableBold, enableItalic]);
  return null;
}

/* =======================
 * Line Break Plugin - converts Enter to <br> instead of new paragraph
 * ======================= */
function LineBreakPlugin() {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      event => {
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          editor.dispatchCommand(INSERT_LINE_BREAK_COMMAND, false);
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor]);
  return null;
}

/* =======================
 * Format Toolbar
 * ======================= */
function FormatToolbar({ enableBold = true, enableItalic = true }) {
  const [editor] = useLexicalComposerContext();
  if (!enableBold && !enableItalic) return null;
  return (
    <div className="email-template-format-toolbar mt-4 mb-2 ml-2">
      {enableBold && (
        <Button
          variant="ghost"
          size="sm"
          onMouseDown={e => e.preventDefault()}
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}
          className="font-bold"
        >
          B
        </Button>
      )}
      {enableItalic && (
        <Button
          variant="ghost"
          size="sm"
          style={{ marginLeft: 8 }}
          onMouseDown={e => e.preventDefault()}
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}
          className="italic"
        >
          I
        </Button>
      )}
    </div>
  );
}

function importFromTokenString(text) {
  return () => {
    const root = $getRoot();
    root.clear();
    const para = $createParagraphNode();

    const regex = /\[#([^\]]+)]/g;
    let idx = 0;
    let m;

    while ((m = regex.exec(text)) !== null) {
      if (m.index > idx) {
        const plain = text.slice(idx, m.index);
        para.append($createTextNode(plain));
      }
      para.append($createTagNode(m[1]));
      para.append($createTextNode(" "));
      idx = regex.lastIndex;
    }
    if (idx < text.length) {
      para.append($createTextNode(text.slice(idx)));
    }

    root.append(para);
  };
}

function importFromHtml(html) {
  return () => {
    const root = $getRoot();
    root.clear();
    const para = $createParagraphNode();

    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // Recursively process nodes
    const processNode = (node, formatFlags = { bold: false, italic: false }) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        if (!text) return;

        // Check for [#field] patterns in text
        const regex = /\[#([^\]]+)]/g;
        let idx = 0;
        let m;

        while ((m = regex.exec(text)) !== null) {
          if (m.index > idx) {
            const plain = text.slice(idx, m.index);
            if (plain) {
              const textNode = $createTextNode(plain);
              if (formatFlags.bold) textNode.toggleFormat("bold");
              if (formatFlags.italic) textNode.toggleFormat("italic");
              para.append(textNode);
            }
          }
          para.append($createTagNode(m[1]));
          idx = regex.lastIndex;
        }
        if (idx < text.length) {
          const remaining = text.slice(idx);
          if (remaining) {
            const textNode = $createTextNode(remaining);
            if (formatFlags.bold) textNode.toggleFormat("bold");
            if (formatFlags.italic) textNode.toggleFormat("italic");
            para.append(textNode);
          }
        }
        return;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();

        // Check if this is a tag span
        if (tagName === "span" && node.getAttribute("data-type") === "tag") {
          const label = node.getAttribute("data-key");
          if (label) {
            para.append($createTagNode(label));
            return;
          }
        }

        // Handle line breaks
        if (tagName === "br") {
          para.append($createLineBreakNode());
          return;
        }

        // Determine formatting based on tag
        const newFlags = { ...formatFlags };
        if (tagName === "b" || tagName === "strong") {
          newFlags.bold = true;
        }
        if (tagName === "i" || tagName === "em") {
          newFlags.italic = true;
        }

        // Process children
        for (const child of node.childNodes) {
          processNode(child, newFlags);
        }
      }
    };

    // Process all children of the parsed HTML
    for (const child of tempDiv.childNodes) {
      processNode(child);
    }

    // If paragraph is empty, add an empty text node
    if (para.getChildrenSize() === 0) {
      para.append($createTextNode(""));
    }

    root.append(para);
  };
}

function isHtmlString(str) {
  return /<[a-z][\s\S]*>/i.test(str);
}

/* =======================
 * Demo Component
 * ======================= */
export default function LexicalTemplate({
  templateFields,
  handleMessageTemplateHTML,
  templateMessageHTML,
  missingFields,
  enableBold = true,
  enableItalic = true
}) {
  // Use ref to store the initial value - this prevents re-initialization on every change
  const initialValueRef = useRef(templateMessageHTML);

  const initialConfig = useMemo(() => {
    const initialValue = initialValueRef.current || "";
    return {
      namespace: "taggable-lexical",
      editorState: editor => {
        const importFn = isHtmlString(initialValue) ? importFromHtml : importFromTokenString;
        editor.update(importFn(initialValue));
      },
      onError(error) {
        console.error(error);
      },
      nodes: [TagNode, LineBreakNode]
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only initialize once

  return (
    <div className="">
      <div className="">
        <LexicalComposer initialConfig={initialConfig}>
          <TagInsertPlugin options={templateFields} missingFields={missingFields} />

          <FormatToolbar enableBold={enableBold} enableItalic={enableItalic} />

          <TextFormatShortcutsPlugin enableBold={enableBold} enableItalic={enableItalic} />

          <LineBreakPlugin />

          <SyncFromPropPlugin value={templateMessageHTML} />

          <div className="rich-text-plugin-container">
            <RichTextPlugin
              contentEditable={<ContentEditable className="template-editor" />}
              ErrorBoundary={LexicalErrorBoundary}
            />
          </div>
          <HistoryPlugin />
          <OnChangePlugin
            onChange={(editorState, editor) => {
              const html = editorState.read(() => $generateHtmlFromNodes(editor));
              handleMessageTemplateHTML({ html });
            }}
          />
        </LexicalComposer>
      </div>
    </div>
  );
}

/* =======================
 * Prop -> Editor sync plugin
 * ======================= */
function SyncFromPropPlugin({ value }) {
  const [editor] = useLexicalComposerContext();
  const lastExternalValueRef = useRef(value);
  const isInternalChangeRef = useRef(false);

  // Mark that we're about to make an internal change (from typing)
  useEffect(() => {
    const removeListener = editor.registerUpdateListener(({ tags }) => {
      // If the update was triggered by user input, mark it as internal
      if (!tags.has("external-sync")) {
        isInternalChangeRef.current = true;
        // Reset after a short delay
        setTimeout(() => {
          isInternalChangeRef.current = false;
        }, 100);
      }
    });
    return removeListener;
  }, [editor]);

  useEffect(() => {
    // Skip if value hasn't changed from last external update
    if (value === lastExternalValueRef.current) return;

    // Skip if this change originated from user typing in the editor
    if (isInternalChangeRef.current) {
      lastExternalValueRef.current = value;
      return;
    }

    // This is an external change (e.g., selecting a different template)
    // Import the new content
    const importFn = isHtmlString(value || "") ? importFromHtml : importFromTokenString;
    editor.update(importFn(value || ""), { tag: "external-sync" });

    lastExternalValueRef.current = value;
  }, [value, editor]);

  return null;
}
